import { Link } from 'react-router-dom';
import { Package, CalendarDays, BookOpen, Gift, Mail, ArrowUpRight, ClipboardList, Share2, Images, KeyRound, Inbox } from 'lucide-react';

const TILES = [
  {
    to: '/admin/orders',
    title: 'Custom orders',
    description: 'See every custom bracelet ordered, the maker\u2019s instructions (bead counts, size, clasp, gift message), and mark each one pending \u2192 in progress \u2192 shipped.',
    icon: ClipboardList,
    accent: '#BC4749',
    testId: 'admin-tile-orders',
  },
  {
    to: '/admin/products',
    title: 'Products',
    description: 'Add, edit and remove bracelets. Upload product photos and gallery images, set occasion tags, and write the 3-part structured notes. Or bulk-import many at once.',
    icon: Package,
    accent: '#D4AF37',
    testId: 'admin-tile-products',
  },
  {
    to: '/admin/markets',
    title: 'Markets',
    description: 'Schedule the markets Lindy will be at next. These show up on the homepage Markets Calendar with Google &amp; Apple "add to calendar" links.',
    icon: CalendarDays,
    accent: '#84A98C',
    testId: 'admin-tile-markets',
  },
  {
    to: '/admin/blog',
    title: 'Blog',
    description: 'Write blog posts — stories, behind-the-scenes, gift guides. Posts appear on the public blog and link from the footer.',
    icon: BookOpen,
    accent: '#52796F',
    testId: 'admin-tile-blog',
  },
  {
    to: '/admin/discounts',
    title: 'Builder discount popup',
    description: 'Set the code, % off, on/off toggle, headline and copy for the discount popup that appears after a visitor designs their own bracelet.',
    icon: Gift,
    accent: '#D4AF37',
    testId: 'admin-tile-discounts',
  },
  {
    to: '/admin/emails',
    title: 'Review-request emails',
    description: 'Send a warm "did your bracelet arrive?" review email to a customer 7–10 days after delivery, with an optional thank-you discount code.',
    icon: Mail,
    accent: '#84A98C',
    testId: 'admin-tile-emails',
  },
  {
    to: '/admin/social',
    title: 'Social media kit',
    description: 'Generate an Instagram + Facebook caption with hashtags, plus ready-to-post 1080×1080 feed and 1080×1920 story images for any product. Carries the LAUNCH20 discount code.',
    icon: Share2,
    accent: '#D4AF37',
    testId: 'admin-tile-social',
  },
  {
    to: '/admin/gallery',
    title: 'Gallery',
    description: 'Curate the public /gallery page. Create custom albums (Studio Moments, Markets, Customer photos…), drop in photographs, set captions, choose a cover for each album.',
    icon: Images,
    accent: '#52796F',
    testId: 'admin-tile-gallery',
  },
  {
    to: '/admin/gallery?tab=submissions',
    title: 'Customer photos · moderation',
    description: 'Review and approve photographs sent in by customers wearing their bracelets. Approved photos land in the Customer Photos album on /gallery.',
    icon: Inbox,
    accent: '#D4AF37',
    testId: 'admin-tile-submissions',
  },
  {
    to: '/admin/account',
    title: 'Account',
    description: 'Change the admin password. Stored as a bcrypt hash in the database so it survives every deploy and container restart.',
    icon: KeyRound,
    accent: '#84A98C',
    testId: 'admin-tile-account',
  },
];

export default function AdminHomePage() {
  return (
    <div className="pt-28 pb-20 min-h-screen" data-testid="admin-home-page">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
        <div className="mb-12 max-w-2xl">
          <p className="text-caption text-[#84A98C] mb-3">Admin</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">Studio &amp; Storefront</h1>
          <p className="text-[#52796F]">
            Everything that's editable on Whispering Wristwear, in one quiet place.
            Pick a section below.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TILES.map((tile) => {
            const Icon = tile.icon;
            return (
              <Link
                key={tile.to}
                to={tile.to}
                className="group relative bg-[#F5F5F0] hover:bg-white border border-[#CAD2C5]/40 hover:border-[#84A98C]/60 rounded-sm p-7 md:p-8 transition-all duration-300"
                data-testid={tile.testId}
              >
                <div className="flex items-start gap-5">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${tile.accent}1A`, color: tile.accent }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <h2 className="font-serif text-xl md:text-2xl text-[#354F52] leading-tight">
                        {tile.title}
                      </h2>
                      <ArrowUpRight
                        className="w-5 h-5 text-[#84A98C] group-hover:text-[#D4AF37] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300 flex-shrink-0"
                        strokeWidth={1.5}
                      />
                    </div>
                    <p className="text-sm text-[#52796F] leading-relaxed">{tile.description}</p>
                    <p className="text-xs text-[#84A98C] mt-3 tracking-widest uppercase">
                      {tile.to}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 pt-8 border-t border-[#CAD2C5]/40">
          <p className="text-caption text-[#84A98C] mb-3">Public helpers</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#52796F]">
            <Link to="/builder" className="hover:text-[#D4AF37] transition-colors">Bracelet Builder →</Link>
            <Link to="/gift-voucher" className="hover:text-[#D4AF37] transition-colors">Gift voucher generator →</Link>
            <Link to="/keepsake-card" className="hover:text-[#D4AF37] transition-colors">Keepsake card (A6 PDF) →</Link>
            <Link to="/custom" className="hover:text-[#D4AF37] transition-colors">Custom order form →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
